import moment from 'moment';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';
import setNullEmptyValues from 'common/util/setNullEmptyValues';
import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO,
  APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO,
} from 'common/householdApplication/applicationFormFields';
import {
  getHouseholdApplicationDataFields,
  shouldReSendVerificationOfRentalAutoText,
  shouldReSendVerificationOfEmploymentAutoText,
} from 'common/householdApplication/applicationHelpers';
import { hasRoleAdmin } from 'common/userRoles';
import {
  RENTAL_APPLICATION_STEP_FORM,
  RENTAL_APPLICATION_STEP_PERSONAL_INFO,
} from 'common/householdApplication/rentalApplicationHelpers';
import makeInviteRoommateLink from 'common/util/makeInviteRoommateLink';
import {
  INCOME_VERIFICATION_STATUS_COMPLETED,
  INCOME_VERIFICATION_STATUS_PENDING,
} from 'common/incomeVerificationConstants';
import {
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_RECENTLY_EMPLOYED,
} from 'common/householdConstants';
import { ID_VERIFICATION_METHOD_VOUCHED } from 'common/idVerificationConstants';

import { getLatestActiveConcession, hasActiveConcession } from 'common/concessionHelpers';

import { getListingAvailableDateRange } from 'common/listingHelpers';

import {
  scrollToFormField,
  getValidFormValues,
  createInitialValues,
  handlePetCountChange,
  handleVehicleCountChange,
  handleChildrenCountChange,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
} from 'client/helpers/sendDataToMobileApp';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withLocationQuery from 'client/helpers/withLocationQuery';

import HouseholdActions from 'client/actions/Household';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import IncomeVerificationActions from 'client/actions/IncomeVerification';

import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrFormFieldGroup, {
  SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP,
} from 'client/components/SrFormFieldGroup';
import SrSeparator from 'client/components/SrSeparator';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrInviteRoommateModal from 'client/components/SrInviteRoommateModal';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrPetScreeningLinkModal from 'client/components/SrPetScreeningLinkModal';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';
import SrRentalApplicationFlowTestForm from 'client/components/SrRentalApplicationFlowTest/SrRentalApplicationFlowTestForm';
import SrRentalApplicationIdVerification from 'client/components/SrRentalApplicationIdVerification/SrRentalApplicationIdVerification';
import SrBox from 'client/components/SrBox';
import SrVerifyIncomeOptions from 'client/components/SrVerifyIncomeOptions/SrVerifyIncomeOptions';
import SrConcessionsBanner from 'client/components/SrConcessionsBanner/SrConcessionsBanner';

@withLocationQuery
@branch({
  userAgent: ['analytics', 'userAgent'],
  currentUser: ['currentUser'],
})
@branch(({ householdApplicationId, guarantorApplicationId }) => ({
  incomeVerification: [
    'incomeVerifications',
    'byApplicationId',
    householdApplicationId || guarantorApplicationId,
  ],
}))
class SrRentalApplicationFlowStepForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
    concessions: PropTypes.array,
    user: PropTypes.object.isRequired,
    renterProfile: PropTypes.object.isRequired,
    householdListing: PropTypes.object,
    householdId: PropTypes.number.isRequired,
    exitRentalApplication: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    householdApplicationId: PropTypes.number.isRequired,
    guarantorApplicationId: PropTypes.number,
    household: PropTypes.object,
    partner: PropTypes.object,
    partnerApplicationQuestions: PropTypes.array,
    userAgent: PropTypes.object.isRequired,
  };

  constructor() {
    super();

    this.state = {
      submittingApplication: false,
      showInviteRoommateModal: false,
      showPetScreeningLinkModal: false,
      retryIncomeVerificationFailed: false,
    };
  }

  componentDidMount() {
    const {
      dispatch,
      listing,
      householdApplicationId,
      guarantorApplicationId,
      goToNextRentalApplicationStep,
    } = this.props;

    this._reloadIncomeVerificationData();

    const { goToFormFieldId } = this.props.locationQuery;
    if (goToFormFieldId) {
      scrollToFormField({ formFieldId: goToFormFieldId });
    }

    dispatch(HouseholdApplicationActions.shouldGoToCheckout, {
      householdApplicationId: householdApplicationId || guarantorApplicationId,
      listingId: listing.id,
    }).then((shouldGoToCheckout) => {
      if (shouldGoToCheckout) {
        goToNextRentalApplicationStep(RENTAL_APPLICATION_STEP_PERSONAL_INFO);
      }
    });

    this._saveFormInterval = global.setInterval(() => {
      this._onSaveForm();
    }, 20000);
  }

  componentWillUnmount() {
    clearInterval(this._saveFormInterval);
  }

  _onClickLink = (link) => {
    const { userAgent } = this.props;
    const isMobileApp = isUserAgentMobileApp(userAgent);

    event.preventDefault();

    if (isMobileApp) {
      sendDataToMobileApp({
        actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
        url: link,
      });
    } else {
      global.open(link, '_blank');
    }
  };

  _onValidateForm = () => {
    const {
      listing,
      user,
      household,
      incomeVerification,
      tenantScreeningCriteria,
      partnerApplicationQuestions,
    } = this.props;

    const isLeadRenter = household?.leadRenterId === user.id;
    const formikBag = this.formik.getFormikBag();
    const formValues = formikBag.values;
    const formType = this._getFormType();
    const formFields = getApplicationFormFields({
      formType: formType,
      formValues: formValues,
      listing,
      tenantScreeningCriteria,
      isLeadRenter,
      uploadAs: user.id,
      incomeVerificationStatus: incomeVerification?.status,
      incomeVerificationProviderEnabled: this._getIncomeVerificationProviderEnabled(),
      additionalQuestions: partnerApplicationQuestions,
    });
    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    log.info({
      formFielIdsWithErrors,
      formErrors,
      formFields,
      formValues,
    });

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    } else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  };

  _onSubmitForm = (values) => {
    const {
      dispatch,
      currentUser,
      user,
      listing,
      householdId,
      householdApplicationId,
      guarantorApplicationId,
      exitRentalApplication,
      partner,
    } = this.props;

    this.setState({ submittingApplication: true });

    const fieldValues = {
      ...values,
      ...this._removeEmptyFieldFilesUrls(values),
    };

    delete fieldValues.incomeVerificationVOIE;

    if (!hasRoleAdmin(currentUser.roles)) {
      delete fieldValues.governmentIdPhoto;
    }

    // If the income provider was successfully connected remove the paystubPhotos
    // to prevent to override the photos that the provider already update the HH application
    if (values.incomeVerificationVOIE) {
      delete fieldValues.paystubPhotos;
    }

    dispatch(HouseholdActions.updateRenterProfile, {
      userId: user.id,
      updates: setNullEmptyValues(fieldValues),
    })
      .then((renterProfile) => {
        if (householdApplicationId) {
          return dispatch(HouseholdActions.submitHouseholdApplication, {
            userId: user.id,
            listingId: listing.id,
            householdApplication: renterProfile,
          });
        }
        if (guarantorApplicationId) {
          return dispatch(HouseholdActions.submitGuarantorApplication, {
            userId: user.id,
            listingId: listing.id,
            householdId: householdId,
            guarantorApplication: renterProfile,
          });
        }
      })
      .then(() => {
        if (partner?.petScreeningUrl && (fieldValues.petCount > 0 || partner.petScreeningNoPets)) {
          this.setState({ showPetScreeningLinkModal: true });
        } else {
          exitRentalApplication();
        }
      });
  };

  _onSaveForm = () => {
    const { dispatch, user, household, listing, currentUser, tenantScreeningCriteria } = this.props;
    const formType = this._getFormType();

    const isLeadRenter = household?.leadRenterId === user.id;
    const updates = setNullEmptyValues(
      getValidFormValues({
        formFields: getApplicationFormFields({
          formType: formType,
          formValues: this.formik.state.values,
          listing,
          uploadAs: user.id,
          tenantScreeningCriteria,
          isLeadRenter,
        }),
        values: this.formik.state.values,
      }),
    );

    if (Object.keys(updates)) {
      const fieldValues = {
        ...updates,
        ...this._removeEmptyFieldFilesUrls(updates),
      };

      delete fieldValues.incomeVerificationVOIE;

      if (!hasRoleAdmin(currentUser.roles)) {
        delete fieldValues.governmentIdPhoto;
      }

      // If the income provider was successfully connected remove the paystubPhotos
      // to prevent to override the photos that the provider already update the HH application
      if (updates.incomeVerificationVOIE) {
        delete fieldValues.paystubPhotos;
      }

      dispatch(HouseholdActions.updateRenterProfile, {
        userId: user.id,
        updates: fieldValues,
      });
    }
  };

  _onSaveFormAndExit = () => {
    this._onSaveForm();
    this.props.exitRentalApplication();
  };

  _getFormType = () => {
    return this.props.guarantorApplicationId
      ? APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO
      : APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO;
  };

  _onDeleteAdditionalProofOfIncomeFile = (fileIndex) => {
    const formikBag = this.formik.getFormikBag();
    const additionalProofOfIncomePhotos = formikBag.values.additionalProofOfIncomePhotos.filter(
      (fileName, index) => index !== fileIndex,
    );

    formikBag.setFieldValue('additionalProofOfIncomePhotos', additionalProofOfIncomePhotos);
    formikBag.setFieldValue(
      'additionalProofOfIncomePhotosCount',
      formikBag.values.additionalProofOfIncomePhotosCount - 1,
    );
  };

  _onChangeEmploymentStatus = (employmentStatus) => {
    const formikBag = this.formik.getFormikBag();

    formikBag.setFieldValue('employmentStatus', employmentStatus);

    if (employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_RECENTLY_EMPLOYED) {
      formikBag.setFieldValue('payRentExplanation', '');
    } else if (employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED) {
      formikBag.setFieldValue('payRentExplanation', '');
    } else {
      formikBag.setFieldValue('currentJobCompany', '');
      formikBag.setFieldValue('currentJobEmployerMailingAddress', '');
      formikBag.setFieldValue('currentJobPosition', '');
      formikBag.setFieldValue('currentJobSupervisorName', '');
      formikBag.setFieldValue('currentJobSupervisorPhoneNumber', '');
      formikBag.setFieldValue('currentJobEmployerEmail', '');
      formikBag.setFieldValue('currentJobStartDate', '');

      formikBag.setFieldValue('previousJobCompany', '');
      formikBag.setFieldValue('previousJobMailingAddress', '');
      formikBag.setFieldValue('previousJobPosition', '');
      formikBag.setFieldValue('previousJobSupervisorName', '');
      formikBag.setFieldValue('previousJobSupervisorPhoneNumber', '');
      formikBag.setFieldValue('previousJobEmployerEmail', '');
      formikBag.setFieldValue('previousJobStartDate', '');
      formikBag.setFieldValue('previousJobEndDate', '');

      formikBag.setFieldValue('explanationOfIncome', '');
    }
  };

  _onShowInviteRoommateModal = () => {
    this.setState({ showInviteRoommateModal: true });
  };

  _onCloseInviteRoommateModal = () => {
    this.setState({ showInviteRoommateModal: false });
  };

  _onShowPetScreeningLinkModal = () => {
    this.setState({ showPetScreeningLinkModal: true });
  };

  _onClosePetScreeningLinkModal = () => {
    const { exitRentalApplication, partner } = this.props;
    this._onClickLink(partner.petScreeningUrl);
    this.setState({ showPetScreeningLinkModal: false });
    exitRentalApplication();
  };

  _reloadIncomeVerificationData = async () => {
    const { dispatch, householdApplicationId, guarantorApplicationId } = this.props;

    const incomeResponse = await dispatch(IncomeVerificationActions.getByApplicationId, {
      householdApplicationId: householdApplicationId || guarantorApplicationId,
      reload: true,
      includeCurrentJobInfo: true,
    });

    const formikBag = this.formik?.getFormikBag();

    if (
      formikBag &&
      (incomeResponse?.status === INCOME_VERIFICATION_STATUS_PENDING ||
        incomeResponse?.status === INCOME_VERIFICATION_STATUS_COMPLETED)
    ) {
      formikBag.setFieldValue('incomeVerificationVOIE', incomeResponse);

      if (
        incomeResponse &&
        incomeResponse.status === INCOME_VERIFICATION_STATUS_COMPLETED &&
        !formikBag.values.currentJobStartDate
      ) {
        const currentJobStartDate = moment(incomeResponse.currentJobStartDate).toISOString();
        formikBag.setFieldValue('currentJobStartDate', currentJobStartDate);
      }
    }
  };

  _submitButtonDisabled = () => {
    const { submittingApplication } = this.state;

    if (submittingApplication) {
      return true;
    }
  };

  _retryIncomeVerification = (retryIncomeVerificationFailed = true) => {
    this.setState({
      retryIncomeVerificationFailed,
    });
  };

  _getIncomeVerificationProviderEnabled = () => {
    const { partner } = this.props;
    return partner.incomeVerificationProviderEnabled;
  };

  _removeEmptyFieldFilesUrls = (values) => {
    return {
      paystubPhotos: lodashCompact(values.paystubPhotos),
      bankStatementFileUrls: lodashCompact(values.bankStatementFileUrls),
      additionalProofOfIncomePhotos: lodashCompact(values.additionalProofOfIncomePhotos),
    };
  };

  _onCompleteIdVerification = (idVerification) => {
    const formikBag = this.formik?.getFormikBag();

    if (formikBag && idVerification) {
      formikBag.setFieldValue('idVerificationMethod', ID_VERIFICATION_METHOD_VOUCHED);
      formikBag.setFieldTouched('idVerificationMethod', true, true);
    }
  };

  render() {
    const {
      user,
      currentUser,
      renterProfile,
      tenantAlertReport,
      householdListing,
      listing,
      concessions,
      tenantScreeningCriteria,
      appUrl,
      householdId,
      householdApplicationId,
      guarantorApplicationId,
      household,
      partner,
      partnerApplicationQuestions,
      incomeVerification,
      dispatch,
    } = this.props;
    const {
      submittingApplication,
      showInviteRoommateModal,
      showPetScreeningLinkModal,
      retryIncomeVerificationFailed,
    } = this.state;

    const incomeVerificationVOIEDefaultValue =
      incomeVerification?.status === INCOME_VERIFICATION_STATUS_PENDING ||
      incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED
        ? incomeVerification
        : null;

    const defaultValues = getHouseholdApplicationDataFields({
      ...renterProfile,
      incomeVerificationVOIE: incomeVerificationVOIEDefaultValue,
    });
    const formType = this._getFormType();
    const roommateInviteLink = makeInviteRoommateLink({
      user,
      listing,
      appUrl,
      householdId,
    });

    const isLeadRenter = household?.leadRenterId === user.id;
    const shouldDisplayConcessionsBanner = hasActiveConcession(concessions);

    return (
      <>
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          validate={(values) => {
            const formFields = getApplicationFormFields({
              formType: formType,
              formValues: values,
              listing,
              uploadAs: user.id,
              tenantScreeningCriteria,
              onShowInviteRoommateModal: this._onShowInviteRoommateModal,
              isLeadRenter,
              incomeVerificationStatus: incomeVerification?.status,
              incomeVerificationProviderEnabled: this._getIncomeVerificationProviderEnabled(),
              additionalQuestions: partnerApplicationQuestions,
            });
            const errors = createFormValidationFn(formFields)(values);
            return errors;
          }}
          initialValues={createInitialValues({
            formFields: getApplicationFormFields({
              formType: formType,
              formValues: defaultValues,
              listing,
              uploadAs: user.id,
              tenantScreeningCriteria,
              isLeadRenter,
              incomeVerificationStatus: incomeVerification?.status,
              incomeVerificationProviderEnabled: this._getIncomeVerificationProviderEnabled(),
              additionalQuestions: partnerApplicationQuestions,
            }),
            defaultValues,
          })}
          onSubmit={this._onSubmitForm}
          validateOnChange
          render={(formikBag) => {
            const listingAvailableDateRange = getListingAvailableDateRange({
              listing,
              tenantScreeningCriteria,
            });
            const startDate = momentWithAustinTimezone(listingAvailableDateRange.startDate).startOf(
              'day',
            );
            const endDate = momentWithAustinTimezone(listingAvailableDateRange.endDate).endOf(
              'day',
            );
            const moveInDateMoment = formikBag.values.moveInDate
              ? momentWithAustinTimezone(formikBag.values.moveInDate).startOf('day')
              : null;
            const startDateFormatted = startDate.format('MMM D');
            const endDateFormatted = endDate.format('MMM D');
            const showMoveInDateWarning =
              moveInDateMoment &&
              (moveInDateMoment.isBefore(startDate) || moveInDateMoment.isAfter(endDate));
            const {
              hasLandlordEmailChanged,
              hasLandlordPhoneChanged,
            } = shouldReSendVerificationOfRentalAutoText({
              householdApplication: renterProfile,
              updates: formikBag.values,
            });

            const {
              hasSupervisorEmailChanged,
              hasSupervisorPhoneNumberChanged,
            } = shouldReSendVerificationOfEmploymentAutoText({
              householdApplication: renterProfile,
              updates: formikBag.values,
            });

            const formFields = getApplicationFormFields({
              formType: formType,
              formValues: formikBag.values,
              listing,
              tenantScreeningCriteria,
              renterProfile,
              tenantAlertReport,
              uploadAs: user.id,
              hasBuyersAgent: householdListing && householdListing.hasBuyersAgent,
              setFieldValue: formikBag.setFieldValue,
              deleteAdditionalProofOfIncomeFile: this._onDeleteAdditionalProofOfIncomeFile,
              onChangeEmploymentStatus: this._onChangeEmploymentStatus,
              onPetCountChange: handlePetCountChange(formikBag),
              onVehicleCountChange: handleVehicleCountChange(formikBag),
              onChildrenCountChange: handleChildrenCountChange(formikBag),
              onShowInviteRoommateModal: this._onShowInviteRoommateModal,
              isLeadRenter,
              incomeVerificationStatus: incomeVerification?.status,
              incomeVerificationProviderEnabled: this._getIncomeVerificationProviderEnabled(),
              additionalQuestions: partnerApplicationQuestions,
              moveInWarningText: showMoveInDateWarning
                ? 'The landlord may be less likely to approve your application if you choose a move-in ' +
                  `date outside of their desired timeframe (${startDateFormatted} - ${endDateFormatted}).`
                : null,
              requirePDFDocs: partner.experienceConfiguration.requirePDFDocs,
              hasLandlordEmailChanged,
              hasLandlordPhoneChanged,
              hasSupervisorEmailChanged,
              hasSupervisorPhoneNumberChanged,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            const formFieldErrors = createFormValidationFn(formFields)(formikBag.values);

            const incomeVerificationProviderEnabled = this._getIncomeVerificationProviderEnabled();

            const childFieldFormGroups = [];
            if (formikBag.values.childrenCount > 0) {
              for (let index = 0; index < formikBag.values.childrenCount; index += 1) {
                childFieldFormGroups.push(
                  <SrFormFieldGroup
                    key={index}
                    title={`dependent ${index + 1}`}
                    formFields={getFormFieldsById([
                      `children[${index}].name`,
                      `children[${index}].age`,
                      `children[${index}].relationship`,
                    ])}
                    formikBag={formikBag}
                    theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                  />,
                );
              }
            }

            const petFieldFormGroups = [];
            if (formikBag.values.petCount > 0) {
              for (let index = 0; index < formikBag.values.petCount; index += 1) {
                petFieldFormGroups.push(
                  <SrFormFieldGroup
                    key={index}
                    title={`pet ${index + 1}`}
                    formFields={getFormFieldsById([
                      `pets[${index}].name`,
                      `pets[${index}].type`,
                      `pets[${index}].weight`,
                      `pets[${index}].breed`,
                      `pets[${index}].color`,
                      `pets[${index}].age`,
                      `pets[${index}].gender`,
                      `pets[${index}].neutered`,
                      `pets[${index}].declawed`,
                      `pets[${index}].rabiesShotsCurrent`,
                      `pets[${index}].isEsa`,
                      `pets[${index}].esaCertificate`,
                      `pets[${index}].photo`,
                    ])}
                    formikBag={formikBag}
                    theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                  />,
                );
              }
            }

            const vehicleFieldFormGroups = [];
            if (formikBag.values.vehicleCount > 0) {
              for (let index = 0; index < formikBag.values.vehicleCount; index += 1) {
                vehicleFieldFormGroups.push(
                  <SrFormFieldGroup
                    key={index}
                    title={`vehicle ${index + 1}`}
                    formFields={getFormFieldsById([
                      `vehicles[${index}].type`,
                      `vehicles[${index}].model`,
                      `vehicles[${index}].color`,
                      `vehicles[${index}].year`,
                      `vehicles[${index}].licensePlateNumber`,
                      `vehicles[${index}].licenseState`,
                      `vehicles[${index}].monthlyPayment`,
                    ])}
                    formikBag={formikBag}
                    theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                  />,
                );
              }
            }

            const isGuarantor = formType === APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO;
            const showVerifyIncomeSection = formikBag.values.hasSourceOfIncome || isGuarantor;
            const showAdminSection = hasRoleAdmin(currentUser.roles);
            let shouldHideCurrentEmploymentQuestions =
              formikBag.values.employmentStatus ===
                HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED ||
              !formikBag.values.employmentStatus;

            if (isGuarantor) {
              shouldHideCurrentEmploymentQuestions = false;
            }

            return (
              <div className="RentalApplicationPage">
                <SrRentalApplicationFlowStepHeader
                  activeStep={3}
                  hideBackButton
                  onSaveAndExit={this._onSaveFormAndExit}
                  isGuarantorApp={guarantorApplicationId && !householdApplicationId}
                />
                <div className="main">
                  <Form className="mainContent">
                    <div>
                      <SrText className="pageHeadline" size="2xl" weight="medium" leading="2xl">
                        Complete your rental application form
                      </SrText>
                      <SrSeparator />
                    </div>
                    {shouldDisplayConcessionsBanner && (
                      <SrConcessionsBanner
                        concession={getLatestActiveConcession(concessions)}
                        rentPrice={listing.price}
                      />
                    )}
                    {isLeadRenter && (
                      <>
                        <SrFormFieldGroup
                          title="move terms"
                          label={`(landlord prefers move-ins between ${startDateFormatted} - ${endDateFormatted})`}
                          formFields={getFormFieldsById(['moveInDate', 'leaseTerm'])}
                          formikBag={formikBag}
                        />
                      </>
                    )}
                    <SrFormFieldGroup
                      title="current rental"
                      formFields={getFormFieldsById([
                        'rentalStatus',
                        'rentalStatusExplanation',
                        'proofOfHomeOwnershipUrl',
                        'currentLandlordName',
                        'currentLandlordEmail',
                        'currentLandlordPhone',
                        'currentMonthlyRent',
                        'currentRentalMoveInDate',
                        'currentRentalMoveOutDate',
                        'currentRentalReasonMovingOut',
                        'hasSourceOfIncome',
                        'statedYearlyIncome',
                        'payRentExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    {showVerifyIncomeSection && (
                      <>
                        <SrBox className="SrFormFieldGroup">
                          <SrText className="SrFormFieldGroupTitle">verify income</SrText>
                        </SrBox>
                        <SrVerifyIncomeOptions
                          dispatch={dispatch}
                          currentUser={user}
                          listing={listing}
                          incomeVerification={incomeVerification}
                          householdApplicationId={householdApplicationId || guarantorApplicationId}
                          retryIncomeVerificationFailed={retryIncomeVerificationFailed}
                          retryIncomeVerification={this._retryIncomeVerification}
                          reloadIncomeVerificationData={this._reloadIncomeVerificationData}
                          formFieldErrors={formFieldErrors}
                          formikBag={formikBag}
                          formFields={formFields}
                          incomeVerificationEnabled={incomeVerificationProviderEnabled}
                          rentalApplicationStep={RENTAL_APPLICATION_STEP_FORM}
                        />
                        <SrBox margin="16px 0 0" />
                        <SrFormFieldGroup
                          formFields={getFormFieldsById(['employmentStatus'])}
                          formikBag={formikBag}
                        />
                        {!shouldHideCurrentEmploymentQuestions && (
                          <SrFormFieldGroup
                            title="employment information"
                            formFields={getFormFieldsById([
                              'currentJobCompany',
                              'currentJobEmployerMailingAddress',
                              'currentJobPosition',
                              'currentJobSupervisorName',
                              'currentJobSupervisorPhoneNumber',
                              'currentJobEmployerEmail',
                              'currentJobStartDate',
                            ])}
                            formikBag={formikBag}
                          />
                        )}
                        {getFormFieldsById(['previousJobCompany'])[0].validations.includes(
                          'required',
                        ) && (
                          <SrFormFieldGroup
                            title="previous employment"
                            formFields={getFormFieldsById([
                              'previousJobCompany',
                              'previousJobPosition',
                              'previousJobMailingAddress',
                              'previousJobSupervisorName',
                              'previousJobSupervisorPhoneNumber',
                              'previousJobEmployerEmail',
                              'previousJobStartDate',
                              'previousJobEndDate',
                            ])}
                            formikBag={formikBag}
                          />
                        )}
                      </>
                    )}
                    <SrRentalApplicationIdVerification
                      listing={listing}
                      currentUser={user}
                      renterProfile={renterProfile}
                      onComplete={this._onCompleteIdVerification}
                    />
                    {formFieldErrors.idVerificationMethod &&
                      formikBag.touched.idVerificationMethod && (
                        <SrErrorMessage>Please verify your ID above.</SrErrorMessage>
                      )}
                    <SrFormFieldGroup
                      title="dependents"
                      formFields={getFormFieldsById(['childrenCount'])}
                      formikBag={formikBag}
                    />
                    {childFieldFormGroups}
                    <SrFormFieldGroup
                      title="pets"
                      label={"(only yours, not your roommate's pets)"}
                      formFields={getFormFieldsById(['petCount'])}
                      formikBag={formikBag}
                    />
                    {petFieldFormGroups}
                    <SrFormFieldGroup
                      title="vehicles"
                      label="(only yours, no roommate vehicles)"
                      formFields={getFormFieldsById(['vehicleCount'])}
                      formikBag={formikBag}
                    />
                    {vehicleFieldFormGroups}
                    <SrFormFieldGroup
                      title="questions"
                      formFields={getFormFieldsById(['hasWaterFilledFurniture'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['renterInsurance'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['inMilitary'])}
                      formikBag={formikBag}
                    />
                    {formikBag.values.inMilitary && (
                      <SrFormFieldGroup
                        formFields={getFormFieldsById(['isMilitaryShortStay'])}
                        formikBag={formikBag}
                      />
                    )}
                    <SrFormFieldGroup
                      theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                      formFields={getFormFieldsById([
                        'renterHasPreviouslyBeenEvicted',
                        'renterHasPreviouslyBeenEvictedExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    {formikBag.values.renterHasPreviouslyBeenEvicted && (
                      <SrFormFieldGroup
                        formFields={getFormFieldsById(['renterHasPreviouslyBeenEvictedDate'])}
                        formikBag={formikBag}
                      />
                    )}
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['renterHasPreviouslyBreachedLeaseContract'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['renterPreviouslyAskedToMoveOut'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                      formFields={getFormFieldsById([
                        'renterPreviouslyFiledForBankruptcy',
                        'renterPreviousBankruptcyExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    {formikBag.values.renterPreviouslyFiledForBankruptcy && (
                      <SrFormFieldGroup
                        formFields={getFormFieldsById(['renterPreviouslyFiledForBankruptcyDate'])}
                        formikBag={formikBag}
                      />
                    )}
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['renterPrevioulsyLostPropertyForeclosure'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                      formFields={getFormFieldsById([
                        'renterPreviouslyHadCreditProblems',
                        'renterPreviousCreditProblemsExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                      formFields={getFormFieldsById([
                        'renterPreviouslyConvitedOfCrime',
                        'renterPreviousCrimeConvictionExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    {formikBag.values.renterPreviouslyConvitedOfCrime && (
                      <SrFormFieldGroup
                        formFields={getFormFieldsById(['renterPreviouslyConvitedOfCrimeDate'])}
                        formikBag={formikBag}
                      />
                    )}
                    <SrFormFieldGroup
                      formFields={getFormFieldsById(['hasRegisteredSexOffenders'])}
                      formikBag={formikBag}
                    />
                    <SrFormFieldGroup
                      theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                      formFields={getFormFieldsById([
                        'hasPendingCriminalMatters',
                        'pendingCriminalMatterExplanation',
                      ])}
                      formikBag={formikBag}
                    />
                    {partnerApplicationQuestions.map(({ fieldId }) => (
                      <SrFormFieldGroup
                        theme={SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP}
                        formFields={getFormFieldsById([`additionalQuestions.${fieldId}`])}
                        formikBag={formikBag}
                        key={fieldId}
                      />
                    ))}
                    <SrFormFieldGroup
                      theme={
                        formType === APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO
                          ? null
                          : SR_FORM_FIELD_GROUP_THEME_RENTAL_APPLICATION_GROUP
                      }
                      title={
                        formType === APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO
                          ? 'agree to terms'
                          : null
                      }
                      formFields={getFormFieldsById(['agreeTermsOfService'])}
                      formikBag={formikBag}
                    />
                    {showAdminSection && (
                      <SrFormFieldGroup
                        title="admin"
                        formFields={getFormFieldsById(['governmentIdPhoto', 'additionalDocuments'])}
                        formikBag={formikBag}
                      />
                    )}
                    <SrStaticFooter size="135-105">
                      <ButtonDeprecated
                        disabled={this._submitButtonDisabled()}
                        onClick={this._onValidateForm}
                      >
                        {submittingApplication ? 'loading...' : 'finish application'}
                      </ButtonDeprecated>
                    </SrStaticFooter>
                  </Form>
                </div>
                <SrRentalApplicationFlowTestForm formikBag={formikBag} />
                {showPetScreeningLinkModal && (
                  <SrPetScreeningLinkModal
                    partner={partner}
                    onClose={this._onClosePetScreeningLinkModal}
                    petCount={formikBag.values.petCount}
                  />
                )}
              </div>
            );
          }}
        />
        <SrInviteRoommateModal
          roommateInviteLink={roommateInviteLink}
          showModal={showInviteRoommateModal}
          onClose={this._onCloseInviteRoommateModal}
        />
      </>
    );
  }
}

SrRentalApplicationFlowStepForm.displayName = 'SrRentalApplicationFlowStepForm';

export default SrRentalApplicationFlowStepForm;
